import { ActionEditorItem } from "@builder/store/slices/actionBuilderSlice";
import { ActionBuilder, ActionType, RenderActionBuilderItem } from "@common/build/types";
import { extractSources, parseConditionString } from "./conditionsHelpers";
import { v4 } from "uuid";
import { ConditionState } from "@builder/hooks/useConditions";
import { ExtensionGUID } from "@builder/types/Extension.type";

export interface FunctionType {
    id: string;
    group: string;
    type: ActionBuilderType;
    disabled?: boolean;
    extensionGuid?: ExtensionGUID;
}

export enum ActionBuilderType {
    // general actions
    START = "start",
    NEW = "new",
    FUNCTION = "function",
    CONDITION = "condition",
    PAGE = "page",
    OPEN_CASH_DRAWER = "OpenCashDrawer",
    STATION_HOME = "stationHome",
    POPUP = "popup",
    NOTIFICATION = "notification",
    CONFIRMATION = "confirmation",
    SWITCH_USER = "switchUser",
    SWITCH_USER_SPECIFIC = "switchUserSpecific",
    SWITCH_USER_ROLE = "switchUserRole",
    SWITCH_USER_DIALOG = "switchUserDialog",

    // function actions
    ALERT = "alert",
    ADD_CART_DISCOUNT = "cartDiscount",
    ADD_PRODUCT_DISCOUNT = "productDiscount",
    ADD_PRODUCT_FEE = "productFee",
    ADD_CART_NOTE = "cartNote",
    ADD_PRODUCT_NOTE = "productNote",
    ADD_CART_FEE = "cartFee",
    ADD_PRODUCT_TO_CART = "addProductToCart",
    CLEAR_CART = "clearCart",
    SLIDE_OUT = "slideOut",
    AUTHENTICATE_USER = "authenticateUser",
    CFD_PAGE = "cfdPage",
    PRINT = "print",
    REFUND = "refund",
    SET_PRODUCT_ACTIVE = "setProductActive",

    CASH_PAYMENT = "cashPayment",
    TAP_TO_PAY = "tapToPay",
    TERMINAL = "terminal",
    MOTO = "moto",
    CUSTOM_PAYMENT = "customPayment",

    ADD_CUSTOM_SALE = "addCustomSale",

    PARK_ORDER = "parkOrder",
    VIEW_PARK_ORDER = "viewParkOrder",
    RESUME_PARK_ORDER = "resumeParkOrder",
    DELETE_PARK_ORDER = "deleteParkOrder",
    RE_ORDER = "reOrder",

    CREATE_CUSTOMER = "createCustomer",
    EDIT_CUSTOMER = "editCustomer",
    ADD_CUSTOMER_NOTE = "addCustomerNote",
    SET_CUSTOMER = "setCustomerField",
    CLEAR_CUSTOMER = "clearCustomer",
    ADD_CUSTOMER = "addCustomer",

    SPLIT_PAYMENT = "splitPayment",
    ADJUST_INVENTORY = "adjustInventory",

    // 3rd party integrations
    AMELIA = "amelia",
    MAILCHIMP = "mailchimp",
    WOO_ORDER_STATUS = "wooOrderStatus",
    SHOPIFY_ORDER_STATUS = "shopifyOrderStatus",
    WEBHOOK = "webhook",
    ZAPIER = "zapier"
}

export const actionsCategoriesForUI: FunctionType[] = [
    // System Actions
    {
        id: v4(),
        type: ActionBuilderType.STATION_HOME,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.PAGE,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.OPEN_CASH_DRAWER,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.POPUP,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.NOTIFICATION,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.PRINT,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.SLIDE_OUT,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.CONFIRMATION,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.CONDITION,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.AUTHENTICATE_USER,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.CFD_PAGE,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.SPLIT_PAYMENT,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.SWITCH_USER,
        group: "System"
    },
    {
        id: v4(),
        type: ActionBuilderType.CLEAR_CART,
        group: "System"
    },

    // PRODUCT
    {
        id: v4(),
        type: ActionBuilderType.ADD_PRODUCT_TO_CART,
        group: "Product"
    },
    {
        id: v4(),
        type: ActionBuilderType.ADD_PRODUCT_NOTE,
        group: "Product"
    },
    {
        id: v4(),
        type: ActionBuilderType.ADD_PRODUCT_FEE,
        group: "Product"
    },
    {
        id: v4(),
        type: ActionBuilderType.ADD_PRODUCT_DISCOUNT,
        group: "Product"
    },
    {
        id: v4(),
        type: ActionBuilderType.SET_PRODUCT_ACTIVE,
        group: "Product"
    },
    {
        id: v4(),
        type: ActionBuilderType.ADJUST_INVENTORY,
        group: "Product"
    },

    // ORDER
    {
        id: v4(),
        type: ActionBuilderType.ADD_CART_NOTE,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.ADD_CART_DISCOUNT,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.ADD_CART_FEE,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.REFUND,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.CASH_PAYMENT,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.TAP_TO_PAY,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.TERMINAL,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.MOTO,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.CUSTOM_PAYMENT,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.PARK_ORDER,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.VIEW_PARK_ORDER,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.RESUME_PARK_ORDER,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.DELETE_PARK_ORDER,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.RE_ORDER,
        group: "Order"
    },
    {
        id: v4(),
        type: ActionBuilderType.ADD_CUSTOM_SALE,
        group: "Order"
    },
    // CUSTOMER
    {
        id: v4(),
        type: ActionBuilderType.ADD_CUSTOMER_NOTE,
        group: "Customer"
    },
    {
        id: v4(),
        type: ActionBuilderType.CLEAR_CUSTOMER,
        group: "Customer"
    },
    {
        id: v4(),
        type: ActionBuilderType.CREATE_CUSTOMER,
        group: "Customer"
    },
    {
        id: v4(),
        type: ActionBuilderType.EDIT_CUSTOMER,
        group: "Customer"
    },
    {
        id: v4(),
        type: ActionBuilderType.ADD_CUSTOMER,
        group: "Customer"
    },

    // Integration
    {
        id: v4(),
        type: ActionBuilderType.WEBHOOK,
        group: "Integration"
    },
    {
        id: v4(),
        type: ActionBuilderType.ZAPIER,
        group: "Integration",
        extensionGuid: ExtensionGUID.ZAPIER
    }
];

export const getIconByType = (type: ActionBuilderType): string => {
    const IconMap: Record<string, string> = {
        function: "./add-function-action.svg",
        condition: "./add-condition-action.svg",
        page: "./add-page-action.svg",
        OpenCashDrawer: "./open-cash-drawer.svg",
        stationHome: "./add-station-home-action.svg",
        popup: "./add-pop-up-action.svg",
        notification: "./add-notification-action.svg",
        confirmation: "./add-confirmation-action.svg",
        new: "action-new-step-icon.svg",
        start: "action-start-icon.svg",

        alert: "./function-show-alert.svg",
        slideOut: "./function-toggle-slideout.svg",
        cartNote: "./function-add-order-note.svg",
        cartDiscount: "./function-add-cart-discount.svg",
        cartFee: "./function-add-cart-fee.svg",
        productNote: "./function-add-note.svg",
        productDiscount: "./function-add-discount.svg",
        productFee: "./function-product-fee.svg",
        addProductToCart: "./function-add-product.svg",
        clearCart: "./function-remove-product.svg",
        authenticateUser: "./function-authenticate.svg",
        cfdPage: "./function-cfd.svg",
        print: "./function-print.svg",
        refund: "./function-refund-action.svg",
        setProductActive: "./function-set-active-product.svg",
        switchUser: "./function-switch-user-icon.svg",

        parkOrder: "./function-park-order.svg",
        viewParkOrder: "./function-view-park-order.svg",
        resumeParkOrder: "./function-resume-park-order.svg",
        deleteParkOrder: "./function-delete-park-order.svg",
        reOrder: "./function-re-order.svg",
        createCustomer: "./function-edit-customer.svg",
        editCustomer: "./function-edit-customer.svg",
        addCustomerNote: "./function-add-customer-note.svg",
        setCustomerField: "./function-set-customer-field.svg",
        clearCustomer: "./function-clear-customer.svg",
        addCustomer: "./function-add-customer-cart.svg",
        addCustomSale: "./function-custom-sale.svg",

        splitPayment: "./function-split-payment.svg",
        adjustInventory: "./function-adjust-inventory.svg",

        cashPayment: "./function-cash-payment.svg",
        tapToPay: "./function-tap-to-pay.svg",
        terminal: "./function-terminal.svg",
        moto: "./function-terminal.svg",
        customPayment: "./function-cash-payment.svg",

        amelia: "./function-amelia-default.svg",
        mailchimp: "./function-mail-chimp.svg",
        wooOrderStatus: "./function-woo-order.svg",
        shopifyOrderStatus: "./function-shopify-order.svg",
        webhook: "./function-webhook.svg",
        zapier: "./function-zapier.svg"
    };

    return IconMap[type] || "";
};

export const getTitleByType = (type: string, isStepper?: boolean): string => {
    const titleMap: Record<string, string> = {
        // general actions
        function: isStepper ? "Action" : "Choose your function",
        condition: "Condition",
        page: "Go to page",
        OpenCashDrawer: "Open Cash Drawer",
        stationHome: "Go to Station Home",
        popup: "Toggle pop up",
        notification: "Notification",
        confirmation: "Confirmation",
        new: "Choose your action",
        start: isStepper ? "Action start" : "Name your action",

        // function actions
        alert: "Show alert",
        cartNote: "Add order note",
        cartFee: "Add cart fee",
        productNote: "Add product note",
        cartDiscount: "Add cart discount",
        productDiscount: "Add product discount",
        productFee: "Add product fee",
        addProductToCart: "Add product to cart",
        clearCart: "Clear cart",
        slideOut: "Toggle slide out",
        authenticateUser: "Authenticate user",
        cfdPage: "Update customer facing display",
        print: "Print",
        refund: "Initiate refund",
        setProductActive: "Set product as active",

        parkOrder: "Park order",
        viewParkOrder: "View parked orders",
        resumeParkOrder: "Resume parked order",
        deleteParkOrder: "Delete parked  order",
        reOrder: "Reorder selected order",
        addCustomSale: "Add custom sale",
        // Customer
        createCustomer: "Create a new customer",
        editCustomer: "Edit customer profile",
        setCustomerField: "Set customer field",
        addCustomerNote: "Add customer note",
        clearCustomer: "Remove customer from the cart",
        addCustomer: "Add customer to the cart",

        cashPayment: "Cash payment",
        tapToPay: "Card - Tap to Pay",
        terminal: "Card - Terminal",
        moto: "Card - MOTO",
        customPayment: "Custom payment",

        splitPayment: "Partial payment",
        adjustInventory: "Adjust inventory",
        // 3rd party
        mailchimp: "Mailchimp",
        amelia: "Amelia",
        wooOrderStatus: "WooCommerce: Order status",
        shopifyOrderStatus: "Shopify: Order status",
        switchUser: "Switch user",
        webhook: "Webhook Trigger (POST)",
        zapier: "Run automation in Zapier"
    };

    return titleMap[type] || "";
};

export const getSubtitleByType = (type: string): string => {
    const subTitleMap: Record<string, string> = {
        condition: "The action will proceed to next step condition is met.",
        stationHome: "This will return the user to Station Home",
        page: "This will open the page that you select below",
        OpenCashDrawer: "This will trigger the cash drawer to open",
        popup: "This will overlay the pop up that you select below",
        notification: "This will notify the user",
        confirmation: "This will request the user to confirm that it is okay to proceed.",
        addCustomerNote: "Add note to the active selected customer.",
        clearCustomer: "This will remove the active customer from the current sale.",
        adjustInventory: "This will adjust the inventory of the active selected product.",
        authenticateUser: "Select which user roles can proceed",
        cfdPage: "Update customer display to a page or a pop up.",
        slideOut: "This will toggle the selected slide out",
        wooOrderStatus: "update active order to this status in WooCommerce.",
        productDiscount: "This will apply a discount to the active selected product.",
        productNote: "This will add a note to the active selected product.",
        cartFee: "Cart fees are added to the order and displayed as a line item.",
        productFee: "Product fees are added to the active product and will be displayed under that specific product in the cart.",
        addProductToCart: "This will add the selected product to the cart.",
        cartDiscount: "This will apply a discount to the cart.",
        cartNote: "Add additional details or special instructions to the order.",
        refund: "Refund the active order",
        setProductActive: "This will set the sku below to active selected product.",
        parkOrder: "Save the active order to resume later in the session.",
        viewParkOrder: "Open the Parked Orders dialog box to resume a parked order",
        resumeParkOrder: "Open a held order for edits or payment",
        deleteParkOrder: "Removes a parked order from the system",
        switchUser: "This will switch the user to the selected user",
        webhook: "This enables you to post to a pre-defined webhook URL.",
        zapier: "Trigger a Zap via Custom Event",
        reOrder: "Bring the selected order back into the current active cart",
        addCustomSale: "This will open the custom sale dialog box.",
        print: "This will trigger printing of the selected print block."
    };

    return subTitleMap[type] || "";
};

export const getFunctionCategory = (type: string): string => {
    const categoryMap: Record<string, string> = {
        System: "System Actions",
        Product: "Product Actions",
        Order: "Order Actions",
        Customer: "Customer Actions",
        Integration: "Integration Actions"
    };

    return categoryMap[type] || "";
};

export const getActionBuilderTitle = (action: RenderActionBuilderItem) => {
    const titleMap: Record<string, string> = {
        // general
        condition: "Condition",
        home: "Go to Station Home",
        stationHome: "Action: Go to Station Home",
        page: "Go to page",
        OpenCashDrawer: "Open Cash Drawer",
        popup: "Toggle pop up",
        notification: "Notification",
        confirmation: "Confirmation",
        new: "What do you want to do?",
        start: "Action start",
        // function actions
        alert: "Action: Show alert",
        cartNote: "Action: Add order note",
        cartDiscount: "Action: Add cart discount",
        cartFee: "Action: Add cart fee",
        productDiscount: "Action: Add product discount",
        productFee: "Action: Add product fee",
        productNote: "Action: Add product note",
        addProductToCart: "Action: Add product to cart",
        clearCart: "Action: Clear cart",
        slideOut: "Action: Slideout",
        authenticateUser: "Action: Authenticate user",
        cfdPage: "Action: Update customer facing display",
        print: "Action: Print",
        refund: "Action: Initiate refund",
        setProductActive: "Action: Set product as active",

        // Customer
        createCustomer: "Action: Create a new customer",
        editCustomer: "Action: Edit customer profile",
        setCustomerField: "Action: Set customer field",
        addCustomerNote: "Action: Add customer note",
        addCustomer: "Action: Add customer to the cart",

        cashPayment: "Action: Cash payment",
        tapToPay: "Action: Card - Tap to Pay",
        terminal: "Action: Card - Terminal",
        moto: "Action: Card - MOTO",
        customPayment: "Action: Custom payment",

        parkOrder: "Action: Park order",
        viewParkOrder: "Action: View parked orders",
        resumeParkOrder: "Action: Resume parked order",
        deleteParkOrder: "Action: Delete parked  order",
        reOrder: "Action: Re-order",
        splitPayment: "Action: Partial payment",
        adjustInventory: "Action: Adjust Inventory",
        addCustomSale: "Action: Add custom sale",
        // 3rd party
        mailchimp: "Action: Mailchimp",
        amelia: "Action: Amelia",
        wooOrderStatus: "WooCommerce: Order status",
        shopifyOrderStatus: "Shopify: Order status",
        switchUser: "Action: Switch user",
        webhook: "Action: Webhook Trigger (POST)",
        zapier: "Action: Run automation in Zapier"
    };

    if (action.type === ActionBuilderType.CONDITION && action.action?.condition) {
        const sources = extractSources(action.action.condition);
        return `Condition: ${sources.join(", ")}`;
    }

    if (action.type === ActionBuilderType.PAGE && action.action?.name) {
        return `Go to page: ${action.action.name}`;
    }

    if (action.type === ActionBuilderType.POPUP && action.action?.target) {
        return `Open popup: ${action.action.target}`;
    }

    return titleMap[action.type] || "";
};

export const transformToActionBuilder = (actionEditorItem: ActionEditorItem): RenderActionBuilderItem => {
    return {
        type: actionEditorItem.type,
        id: actionEditorItem.id!, //no way id will be undefined at this point
        action: actionEditorItem.action || null
    };
};

export const reconstructActionEditor = (actionBuilder: ActionBuilder): ActionEditorItem[] => {
    const startAction = {
        type: ActionBuilderType.START,
        id: actionBuilder.id,
        name: actionBuilder.name,
        subtitle: null,
        title: getTitleByType(ActionBuilderType.START, true),
        action: actionBuilder.trigger ? { condition: actionBuilder.trigger.condition } : null,
        isAuto: actionBuilder.isAuto
    };

    const otherActions = actionBuilder.actions.map(actionBuilder => ({
        type: actionBuilder.type as ActionBuilderType,
        id: actionBuilder.id,
        title: getActionBuilderTitle(actionBuilder),
        subtitle: null,
        action: actionBuilder.action
    }));

    const actionEditor = [startAction, ...otherActions];

    return actionEditor;
};

export const validateData = <T>(action: { data?: T; condition?: string }, actionType: ActionType | undefined): boolean => {
    if (actionType === ActionType.CONDITION) {
        let defaultValues: ConditionState | undefined = action.condition ? parseConditionString(action.condition) : undefined;
        if (defaultValues?.conditions[0].length === 0) {
            return false;
        }
    }

    const data = action.data;

    if (typeof data === "object") {
        if ((data as any).isFixed) {
            for (const key in data) {
                if (key !== "isFixed" && key !== "isPercent" && ((data as any)[key] === "" || (data as any)[key] === null)) {
                    return false;
                }
            }
        }
    }

    return true;
};
